import { useState } from 'react';
import type { FormEvent } from 'react';

/*
 * TEMPLATE CONFIG — form receiver.
 * This endpoint belongs to the project it was created in. After copying the template,
 * replace it with your own form endpoint (e.g. via the Forms panel) so submissions
 * reach your account. The receiver must reply with JSON { code: 'OK' } on success.
 */
const SUBMIT_URL = 'https://readdy.ai/api/form/db3fh00m252sfkho2a10';
const MAX_TEXT = 500;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type Status = 'idle' | 'submitting' | 'success' | 'error';
type Errors = Partial<Record<'name' | 'email' | 'company' | 'workflow' | 'systems' | 'better', string>>;

const inputBase = 'mt-2 w-full border bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-foreground-500 hover:border-primary-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:opacity-60';

function borderFor(err?: string) {
  return err ? 'border-secondary-600' : 'border-primary-300';
}

function Label({ htmlFor, text, optional }: { htmlFor: string; text: string; optional?: boolean }) {
  return (
    <label htmlFor={htmlFor} className="flex items-baseline justify-between font-label text-[11px] uppercase tracking-[0.12em] text-foreground-700">
      <span>{text}</span>
      <span className="text-foreground-400">{optional ? 'Optional' : 'Required'}</span>
    </label>
  );
}

function FieldError({ id, msg }: { id: string; msg?: string }) {
  return (
    <p id={id} role="alert" className={`mt-1.5 min-h-0 text-xs text-secondary-700 ${msg ? '' : 'hidden'}`}>
      <span>{msg || ''}</span>
    </p>
  );
}

export default function ConsultationForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState('');
  const [counts, setCounts] = useState({ workflow: 0, better: 0 });

  const validate = (fd: FormData): Errors => {
    const e: Errors = {};
    const v = (k: string) => String(fd.get(k) ?? '').trim();
    if (!v('name')) e.name = 'Please enter your name.';
    if (!v('email')) e.email = 'Please enter your work email.';
    else if (!EMAIL_RE.test(v('email'))) e.email = 'Please enter a valid email address.';
    if (!v('company')) e.company = 'Please enter your company.';
    if (!v('workflow')) e.workflow = 'Tell us which workflow is slowing you down.';
    else if (v('workflow').length > MAX_TEXT) e.workflow = `Please keep this under ${MAX_TEXT} characters.`;
    if (v('better').length > MAX_TEXT) e.better = `Please keep this under ${MAX_TEXT} characters.`;
    return e;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;
    const form = event.currentTarget;
    const fd = new FormData(form);

    const trap = String(fd.get('company_alt') ?? '').trim();
    if (trap) {
      setStatus('success');
      return;
    }
    fd.delete('company_alt');

    const found = validate(fd);
    setErrors(found);
    setFormError('');
    if (Object.keys(found).length) {
      setStatus('idle');
      const first = form.querySelector<HTMLElement>(`[name="${Object.keys(found)[0]}"]`);
      first?.focus();
      return;
    }

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      const s = String(value).trim();
      if (s) params.append(key, s);
    });

    setStatus('submitting');
    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: unknown } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const detail = typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '';
      const serverMsg = parsed?.meta?.message || parsed?.message || detail || (parsed ? '' : responseText);
      const isSpam = /spam/i.test(`${serverMsg} ${detail} ${responseText}`);

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        form.reset();
        setCounts({ workflow: 0, better: 0 });
        setStatus('success');
        return;
      }
      setFormError(serverMsg || 'Something went wrong. Please try again.');
      setStatus('error');
    } catch {
      setFormError('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div role="status" className="border border-primary-200 bg-background-50 p-6 md:p-8">
        <p className="font-label text-[11px] uppercase tracking-[0.14em] text-primary-500">Received</p>
        <h2 className="mt-4 text-2xl font-light tracking-[-0.015em] text-foreground-950">Thanks for reaching out.</h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700">Your request has been submitted successfully.</p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="cursor-target mt-8 cursor-pointer whitespace-nowrap border-b border-primary-500 pb-1 text-sm text-primary-600 hover:text-primary-700"
        >
          Send another workflow
        </button>
      </div>
    );
  }

  const busy = status === 'submitting';
  const clearErr = (k: keyof Errors) => {
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  return (
    <form id="consultation-form" data-readdy-form noValidate translate="no" onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <Label htmlFor="cf-name" text="Name" />
          <input id="cf-name" name="name" type="text" autoComplete="name" disabled={busy} aria-invalid={!!errors.name} aria-describedby="cf-name-err" onInput={() => clearErr('name')} className={`${inputBase} ${borderFor(errors.name)}`} />
          <FieldError id="cf-name-err" msg={errors.name} />
        </div>
        <div>
          <Label htmlFor="cf-email" text="Work email" />
          <input id="cf-email" name="email" type="email" autoComplete="email" disabled={busy} aria-invalid={!!errors.email} aria-describedby="cf-email-err" onInput={() => clearErr('email')} className={`${inputBase} ${borderFor(errors.email)}`} />
          <FieldError id="cf-email-err" msg={errors.email} />
        </div>
      </div>

      <div>
        <Label htmlFor="cf-company" text="Company" />
        <input id="cf-company" name="company" type="text" autoComplete="organization" disabled={busy} aria-invalid={!!errors.company} aria-describedby="cf-company-err" onInput={() => clearErr('company')} className={`${inputBase} ${borderFor(errors.company)}`} />
        <FieldError id="cf-company-err" msg={errors.company} />
      </div>

      <div>
        <Label htmlFor="cf-workflow" text="What workflow is slowing you down?" />
        <textarea id="cf-workflow" name="workflow" rows={4} maxLength={MAX_TEXT} disabled={busy} aria-invalid={!!errors.workflow} aria-describedby="cf-workflow-err" onInput={(e) => { const len = e.currentTarget.value.length; clearErr('workflow'); setCounts((c) => ({ ...c, workflow: len })); }} className={`${inputBase} resize-none ${borderFor(errors.workflow)}`} />
        <div className="flex justify-between gap-4">
          <FieldError id="cf-workflow-err" msg={errors.workflow} />
          <span className="ml-auto mt-1.5 font-label text-[10px] text-foreground-400"><span>{`${counts.workflow}/${MAX_TEXT}`}</span></span>
        </div>
      </div>

      <div>
        <Label htmlFor="cf-systems" text="Systems involved" optional />
        <input id="cf-systems" name="systems" type="text" placeholder="e.g. HubSpot, Gmail, Google Sheets" disabled={busy} className={`${inputBase} ${borderFor()}`} />
      </div>

      <div>
        <Label htmlFor="cf-better" text="What would better look like?" optional />
        <textarea id="cf-better" name="better" rows={3} maxLength={MAX_TEXT} disabled={busy} aria-invalid={!!errors.better} aria-describedby="cf-better-err" onInput={(e) => { const len = e.currentTarget.value.length; clearErr('better'); setCounts((c) => ({ ...c, better: len })); }} className={`${inputBase} resize-none ${borderFor(errors.better)}`} />
        <div className="flex justify-between gap-4">
          <FieldError id="cf-better-err" msg={errors.better} />
          <span className="ml-auto mt-1.5 font-label text-[10px] text-foreground-400"><span>{`${counts.better}/${MAX_TEXT}`}</span></span>
        </div>
      </div>

      <div className="cf-extra-field" aria-hidden="true">
        <label htmlFor="cf-company-alt">Company alt</label>
        <input id="cf-company-alt" name="company_alt" type="text" tabIndex={-1} autoComplete="off" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
      </div>

      {status === 'error' && (
        <p role="alert" className="border-l-2 border-secondary-600 bg-secondary-50 px-3 py-2.5 text-sm text-secondary-900">
          <span>{formError}</span>
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="cursor-target mt-2 flex w-full cursor-pointer items-center justify-between whitespace-nowrap bg-primary-500 px-6 py-4 text-sm text-background-50 transition-colors hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-70 dark:text-foreground-950"
      >
        <span>{busy ? 'Sending…' : 'Discuss this Workflow'}</span>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={busy ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} />
        </span>
      </button>
    </form>
  );
}